import {
  ApiError,
  SessionExpiredError,
} from "@/lib/api-client/authenticatedClient";

export const DEFAULT_ERROR_MESSAGE = "Something went wrong. Please try again.";

interface ExtractApiErrorMessageOptions {
  showRawMessage?: boolean;
}

export function extractApiErrorMessage(
  err: unknown,
  options?: ExtractApiErrorMessageOptions,
): string {
  if (err instanceof SessionExpiredError) {
    return err.message;
  }

  if (!options?.showRawMessage) {
    return DEFAULT_ERROR_MESSAGE;
  }

  return extractRawApiErrorMessage(err) ?? DEFAULT_ERROR_MESSAGE;
}

function extractRawApiErrorMessage(err: unknown): string | undefined {
  if (err instanceof ApiError) {
    return formatApiErrorBody(err.body) ?? err.message ?? undefined;
  }
  if (err instanceof Error) {
    return err.message || undefined;
  }
  return undefined;
}

function formatApiErrorBody(body: unknown): string | undefined {
  if (
    !body ||
    typeof body !== "object" ||
    !("title" in body) ||
    typeof body.title !== "string"
  ) {
    return undefined;
  }

  const errors =
    "errors" in body && body.errors && typeof body.errors === "object"
      ? (body.errors as Record<string, unknown>)
      : undefined;

  if (errors) {
    const pathError = getFirstErrorMessage(
      Object.entries(errors).filter(([key]) => key.startsWith("$")),
    );
    if (pathError) return pathError;

    const fieldError = getFirstErrorMessage(
      Object.entries(errors).filter(([key]) => key !== "request"),
    );
    if (fieldError) return fieldError;

    const anyError = getFirstErrorMessage(Object.entries(errors));
    if (anyError) return anyError;
  }

  return body.title;
}

function getFirstErrorMessage(
  entries: [string, unknown][],
): string | undefined {
  for (const [, value] of entries) {
    if (Array.isArray(value) && typeof value[0] === "string") {
      return value[0];
    }
  }
  return undefined;
}
